/**
 * Disease Outbreak Card Component
 * Displays active disease outbreak information with trend
 */

import React from "react";
import {
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  MapPin,
  Calendar,
} from "lucide-react";
import type { DiseaseOutbreak } from "@/services/api/healthRecommendations";
import { useI18nStore } from "@/stores/i18nStore";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";

interface DiseaseOutbreakCardProps {
  outbreak: DiseaseOutbreak;
}

export const DiseaseOutbreakCard: React.FC<DiseaseOutbreakCardProps> = ({
  outbreak,
}) => {
  const t = useI18nStore((state) => state.t);
  const fmt = useLocaleFormat();

  const severityLabels: Record<string, string> = {
    critical: t.outbreakSeverityCritical,
    high: t.outbreakSeverityHigh,
    medium: t.outbreakSeverityMedium,
    low: t.outbreakSeverityLow,
  };
  const severityLabel = (
    severityLabels[outbreak.severity] ?? outbreak.severity
  ).toLocaleUpperCase(fmt.locale);

  const getSeverityConfig = (severity: string) => {
    switch (severity) {
      case "critical":
        return {
          bg: "bg-red-50",
          border: "border-red-300",
          badge: "bg-red-600 text-white",
          text: "text-red-900",
        };
      case "high":
        return {
          bg: "bg-orange-50",
          border: "border-orange-300",
          badge: "bg-orange-600 text-white",
          text: "text-orange-900",
        };
      case "medium":
        return {
          bg: "bg-yellow-50",
          border: "border-yellow-300",
          badge: "bg-yellow-600 text-white",
          text: "text-yellow-900",
        };
      default:
        return {
          bg: "bg-primary-50",
          border: "border-primary-300",
          badge: "bg-primary-600 text-white",
          text: "text-primary-900",
        };
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case "increasing":
        return <TrendingUp className="h-4 w-4 text-red-600" />;
      case "decreasing":
        return <TrendingDown className="h-4 w-4 text-green-600" />;
      default:
        return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTrendText = (trend: string) => {
    switch (trend) {
      case "increasing":
        return t.increasing;
      case "decreasing":
        return t.decreasing;
      default:
        return t.stable;
    }
  };

  const config = getSeverityConfig(outbreak.severity);

  return (
    <div className={`${config.bg} border-2 ${config.border} rounded-lg p-4`}>
      <div className="flex items-start gap-4">
        {/* Alert Icon */}
        <div className="flex-shrink-0">
          <div className={`p-3 rounded-lg ${config.badge}`}>
            <AlertTriangle className="h-6 w-6" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className={`text-xl font-bold ${config.text}`}>
                {outbreak.disease}
              </h3>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span>{outbreak.country}</span>
                  {outbreak.region && <span>• {outbreak.region}</span>}
                </div>
              </div>
            </div>

            <span
              className={`px-3 py-1 rounded-full text-sm font-semibold ${config.badge}`}
            >
              {severityLabel}
            </span>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            {/* Cases Reported */}
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-500 mb-1">
                {t.casesReported}
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {fmt.number(outbreak.casesReported)}
              </div>
            </div>

            {/* Trend */}
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-500 mb-1">{t.trend}</div>
              <div className="flex items-center gap-2">
                {getTrendIcon(outbreak.trendDirection)}
                <span className="text-lg font-semibold text-gray-900">
                  {getTrendText(outbreak.trendDirection)}
                </span>
              </div>
            </div>
          </div>

          {/* Travel Impact */}
          {outbreak.affectsTravel && (
            <div className="mt-3 p-3 bg-white border border-orange-200 rounded-lg">
              <div className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium text-sm">{t.mayAffectTravel}</span>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <Calendar className="h-3 w-3" />
              <span>
                {t.lastReport}: {fmt.date(outbreak.lastReportDate)}
              </span>
            </div>
            <div>
              {t.source}: {outbreak.source.name}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
